import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  useTheme,
  useMediaQuery,
  Paper,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  LocalLaundryService, // Textile
  Pets, // Tannery
  Opacity, // Dairy
  Description, // Paper
  Biotech, // Pharma
  DirectionsCar, // Automobile
  Restaurant, // Food
  Factory, // Refinery
  Recycling // Plastic Recycling
} from '@mui/icons-material';

// Styled components
const IndustryCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
  borderRadius: 16,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
}));

const IconWrapper = styled(Box)(({ theme, bgcolor }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  background: bgcolor || theme.palette.primary.main,
  color: '#fff',
}));

const GradientBackground = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0099cc 0%, #00c6cf 100%)',
  padding: theme.spacing(6, 0),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const IndustryDescription = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

// Industry data
const industries = [
  {
    name: 'Textile',
    icon: <LocalLaundryService sx={{ fontSize: 40 }} />,
    description: 'Advanced water treatment solutions for textile manufacturers, reducing chemical usage and environmental impact while improving process efficiency.',
    color: '#FF5252', // Red
  },
  {
    name: 'Tannery',
    icon: <Pets sx={{ fontSize: 40 }} />,
    description: 'Specialized treatment systems for tannery effluents, addressing high organic loads and chemical contaminants to meet strict environmental regulations.',
    color: '#8D6E63', // Brown
  },
  {
    name: 'Dairy',
    icon: <Opacity sx={{ fontSize: 40 }} />,
    description: 'Comprehensive water management for dairy processing, including CIP recovery, whey processing, and effluent treatment for water reuse.',
    color: '#42A5F5', // Blue
  },
  {
    name: 'Paper',
    icon: <Description sx={{ fontSize: 40 }} />,
    description: 'Tailored solutions for pulp and paper mills, focusing on fiber recovery, chemical reclamation, and closed-loop water systems.',
    color: '#FFA726', // Orange
  },
  {
    name: 'Pharma',
    icon: <Biotech sx={{ fontSize: 40 }} />,
    description: 'High-purity water systems and waste treatment for pharmaceutical manufacturing, ensuring compliance with GMP standards and zero liquid discharge.',
    color: '#66BB6A', // Green
  },
  {
    name: 'Automobile',
    icon: <DirectionsCar sx={{ fontSize: 40 }} />,
    description: 'Specialized treatment for automotive manufacturing wastewater, addressing oils, heavy metals, and painting process effluents.',
    color: '#5C6BC0', // Indigo
  },
  {
    name: 'Food',
    icon: <Restaurant sx={{ fontSize: 40 }} />,
    description: 'Food-grade water purification and wastewater treatment systems designed for the unique challenges of food processing facilities.',
    color: '#EC407A', // Pink
  },
  {
    name: 'Refinery',
    icon: <Factory sx={{ fontSize: 40 }} />,
    description: 'Advanced treatment technologies for refinery and petrochemical wastewater, focusing on oil recovery and difficult-to-treat contaminants.',
    color: '#7E57C2', // Purple
  },
  {
    name: 'Plastic Recycling',
    icon: <Recycling sx={{ fontSize: 40 }} />,
    description: 'Water-efficient solutions for plastic recycling operations, minimizing water usage while effectively treating process water for reuse.',
    color: '#26A69A', // Teal
  },
];

const IndustriesWeServe = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Box mb={6} textAlign="center">
        <Typography 
          variant="h2" 
          component="h1" 
          fontWeight="bold" 
          gutterBottom
          sx={{
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            background: 'linear-gradient(45deg, #0099cc 30%, #00c6cf 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          INDUSTRIES WE SERVE
        </Typography>
        <Typography 
          variant="h5" 
          color="textSecondary" 
          sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
        >
          Specialized water and wastewater treatment solutions tailored to the unique challenges of diverse industries
        </Typography>
        <Divider sx={{ width: '10%', mx: 'auto', borderWidth: 2, borderColor: theme.palette.primary.main, mb: 6 }} />
      </Box>

      <GradientBackground>
        <Container>
          <Typography 
            variant="h4" 
            component="h2" 
            fontWeight="bold" 
            color="white" 
            textAlign="center" 
            gutterBottom
          >
            Our Industry Expertise
          </Typography>
          <Typography 
            variant="body1" 
            color="white" 
            textAlign="center" 
            sx={{ maxWidth: 800, mx: 'auto', mb: 4, opacity: 0.9 }}
          >
            With decades of experience across multiple sectors, we deliver customized water treatment solutions that address industry-specific challenges while optimizing operational efficiency.
          </Typography>
        </Container>
      </GradientBackground>

      <Grid container spacing={4}>
        {industries.map((industry, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <IndustryCard elevation={3}>
              <IconWrapper bgcolor={industry.color}>
                {industry.icon}
              </IconWrapper>
              <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                {industry.name}
              </Typography>
              <IndustryDescription variant="body2">
                {industry.description}
              </IndustryDescription>
            </IndustryCard>
          </Grid>
        ))}
      </Grid>

      <Paper 
        elevation={0} 
        sx={{ 
          mt: 8, 
          p: 4, 
          borderRadius: 4,
          background: 'linear-gradient(to right, rgba(0, 198, 207, 0.1), rgba(0, 153, 204, 0.1))',
          border: '1px solid rgba(0, 198, 207, 0.2)',
        }}
      >
        <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom textAlign="center">
          Why Choose Our Industry-Specific Solutions?
        </Typography>
        <Grid container spacing={4} mt={2}>
          <Grid item xs={12} md={4}>
            <Box textAlign="center" p={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Tailored Approach
              </Typography>
              <Typography variant="body2">
                We understand that each industry faces unique water challenges. Our solutions are specifically designed to address your sector's particular requirements.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box textAlign="center" p={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Regulatory Compliance
              </Typography>
              <Typography variant="body2">
                Stay ahead of increasingly stringent environmental regulations with treatment systems designed to meet or exceed compliance requirements for your industry.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box textAlign="center" p={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Resource Optimization
              </Typography>
              <Typography variant="body2">
                Our solutions focus on water reuse, energy efficiency, and chemical reduction to minimize your environmental footprint while reducing operational costs.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Box mt={8} textAlign="center">
        <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
          Need a Solution for Your Industry?
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 700, mx: 'auto', mb: 4 }}>
          Whether you're in one of our core industries or have unique water treatment challenges in another sector, our team of experts is ready to develop a customized solution for your specific needs.
        </Typography>
        <Box 
          component="a" 
          href="/contact" 
          sx={{
            display: 'inline-block',
            py: 2,
            px: 4,
            bgcolor: theme.palette.primary.main,
            color: 'white',
            borderRadius: 2,
            fontWeight: 'bold',
            textDecoration: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
              transform: 'translateY(-3px)',
              boxShadow: theme.shadows[4],
            }
          }}
        >
          Contact Our Industry Specialists
        </Box>
      </Box>
    </Container>
  );
};

export default IndustriesWeServe;
